/**
 * Product card custom element.
 *
 * Enhances server-rendered product cards with:
 * - Swatch clicking for in-place variant switching (image, price, badges, URL)
 * - Lazy hover image loading on first mouseenter
 *
 * Without JS, swatch links navigate to the variant URL (standard <a> behavior).
 * This is progressive enhancement only.
 */
class ProductCard extends HTMLElement {
  #swatchToken = 0;

  connectedCallback() {
    const hasHover = window.matchMedia('(hover: hover)').matches;

    if (hasHover) {
      const overlay = this.querySelector('.product-card__link-overlay');
      if (overlay) {
        overlay.addEventListener('mouseenter', () => this.#loadHover());
      }
    }

    const swatches = this.querySelectorAll('.product-card__swatch');
    for (const swatch of swatches) {
      if (hasHover) {
        swatch.addEventListener('mouseenter', () => {
          if (swatch.dataset.imageUrl) (new Image()).src = swatch.dataset.imageUrl;
        });
      }
      swatch.addEventListener('click', (e) => this.#selectSwatch(e, swatch));
    }
  }

  #ref(selector) {
    return this.querySelector(selector);
  }

  get #primaryImg() { return this.#ref('.product-card__img--primary'); }
  get #hoverImg() { return this.#ref('.product-card__img--hover'); }
  get #titleLink() { return this.#ref('.product-card__title-link'); }
  get #priceEl() { return this.#ref('[data-slot="product-card-price"] > span'); }
  get #compareAtEl() { return this.#ref('.product-card__price--compare'); }
  get #soldOutBadge() { return this.#ref('.product-card__badge--sold-out'); }
  get #saleBadge() { return this.#ref('.product-card__badge--sale'); }
  get #swatchesEl() { return this.#ref('.product-card__swatches'); }
  get #colourName() { return this.#ref('.product-card__colour-name'); }

  #buildSrcset(url) {
    if (!url) return '';
    return [240, 352, 600, 832, 1200].map(w =>
      url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w'
    ).join(', ');
  }

  #loadHover() {
    const hoverImg = this.#hoverImg;
    if (!hoverImg || !hoverImg.dataset.hoverSrc) return;
    if (hoverImg.getAttribute('src')) return;
    hoverImg.src = hoverImg.dataset.hoverSrc;
  }

  #selectSwatch(event, el) {
    const imageUrl = el.dataset.imageUrl;
    const hoverImageUrl = el.dataset.hoverImageUrl;
    const variantId = el.dataset.variantId;
    const price = el.dataset.price;
    const compareAt = el.dataset.compareAtPrice;
    const onSale = el.dataset.onSale === 'true';
    const available = el.dataset.available === 'true';

    const primaryImg = this.#primaryImg;
    const hoverImg = this.#hoverImg;
    const titleLink = this.#titleLink;
    const priceEl = this.#priceEl;
    const compareAtEl = this.#compareAtEl;
    const soldOutBadge = this.#soldOutBadge;
    const saleBadge = this.#saleBadge;
    const swatchesEl = this.#swatchesEl;
    const colourName = this.#colourName;

    // Don't interfere if another component already handled this event
    if (event.defaultPrevented) return;

    // Preserve standard link semantics for modified clicks
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    // If the component cannot complete a coherent in-place update, fall through to link navigation
    if (!swatchesEl || !titleLink || !priceEl || !compareAtEl || !soldOutBadge || !saleBadge) {
      return;
    }

    if (imageUrl && !primaryImg) {
      return;
    }

    const commitState = () => {
      // Active swatch state
      for (const s of swatchesEl.querySelectorAll('.product-card__swatch')) {
        s.classList.remove('product-card__swatch--active');
        s.removeAttribute('aria-current');
      }
      el.classList.add('product-card__swatch--active');
      el.setAttribute('aria-current', 'true');

      // Colour name
      if (colourName) colourName.textContent = el.dataset.swatchName;

      // URL
      if (variantId) {
        titleLink.href = titleLink.href.split('?')[0] + '?variant=' + variantId;
      }

      // Price
      priceEl.textContent = price;
      priceEl.classList.toggle('product-card__price--sale', onSale);
      compareAtEl.textContent = compareAt;
      compareAtEl.toggleAttribute('hidden', !onSale);

      // Badges: sold out wins over sale
      soldOutBadge.toggleAttribute('hidden', available);
      saleBadge.toggleAttribute('hidden', !onSale || !available);

      // Hover image
      if (hoverImg) {
        hoverImg.dataset.hoverSrc = hoverImageUrl || '';
        hoverImg.removeAttribute('src');
        hoverImg.classList.toggle('product-card__img--hover-available', !!hoverImageUrl);
      }

      this.#loadHover();
    };

    event.preventDefault();

    // Primary image: defer state until target image loads
    if (imageUrl && primaryImg) {
      this.#swatchToken++;
      const token = this.#swatchToken;
      const loader = new Image();
      loader.onload = () => {
        if (this.#swatchToken !== token) return;
        primaryImg.src = imageUrl;
        primaryImg.srcset = this.#buildSrcset(imageUrl);
        primaryImg.alt = el.dataset.imageAlt || '';
        commitState();
      };
      loader.onerror = () => {
        if (this.#swatchToken !== token) return;
        window.location.href = el.href;
      };
      loader.src = imageUrl;
      return;
    }

    commitState();
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
